import React from "react";

// Base shimmer block — adapts to light/dark via CSS vars
const Sk = ({ className = "" }) => (
  <div className={`skeleton-block rounded ${className}`} />
);

// ──────────────────────────────────────────────
// Simple list pages: Genres, Directors, Year, etc.
// Matches: mb-3 bg-white px-4 py-2 rounded-lg border rows
// ──────────────────────────────────────────────
export const ListPageSkeleton = ({ count = 8 }) => (
  <div>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="mb-3 px-4 py-3 rounded-lg border"
        style={{ background: "var(--surface)" }}
      >
        <div className="flex justify-between items-center">
          <Sk className="h-4 w-40" />
          <div className="flex gap-3">
            <Sk className="w-8 h-8 rounded-full" />
            <Sk className="w-8 h-8 rounded-full" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ──────────────────────────────────────────────
// MainPage: grid of page cards (3 cols lg, 2 sm)
// Matches: py-4 px-5 max-h-64 border rounded-lg shadow-lg
// ──────────────────────────────────────────────
export const MainPageSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="py-4 px-5 border rounded-lg shadow-lg"
        style={{ background: "var(--surface)" }}
      >
        {/* Card header: page name + 4 action icons */}
        <div className="flex justify-between items-center mb-4">
          <Sk className="h-5 w-36" />
          <div className="flex gap-3">
            <Sk className="w-5 h-5 rounded" />
            <Sk className="w-5 h-5 rounded" />
            <Sk className="w-5 h-5 rounded" />
            <Sk className="w-5 h-5 rounded" />
          </div>
        </div>
        {/* Row list */}
        <div className="space-y-3">
          <Sk className="h-8 w-full rounded-md" />
          <Sk className="h-8 w-full rounded-md" />
          <Sk className="h-8 w-5/6 rounded-md" />
        </div>
      </div>
    ))}
  </div>
);

// ──────────────────────────────────────────────
// TileList: table rows (Poster | Title | Source | Language | Actions)
// ──────────────────────────────────────────────
export const TileListSkeleton = ({ count = 10 }) => (
  <div className="w-full border rounded-lg overflow-hidden">
    <table className="min-w-full text-sm text-center border border-gray-200">
      <thead style={{ background: "var(--surface-2)" }}>
        <tr>
          {["Poster", "Title", "Source", "Language", "Actions"].map((h) => (
            <th key={h} className="py-2 px-3 border font-semibold text-left">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: count }).map((_, i) => (
          <tr key={i} style={{ background: "var(--surface)" }}>
            {/* Poster */}
            <td className="py-1 px-1 border">
              <Sk className="w-[130px] h-[70px] rounded mx-auto" />
            </td>
            {/* Title */}
            <td className="py-2 px-3 border">
              <Sk className="h-4 w-28 mx-auto" />
            </td>
            {/* Source */}
            <td className="py-2 px-3 border">
              <Sk className="h-4 w-20 mx-auto" />
            </td>
            {/* Language */}
            <td className="py-2 px-3 border">
              <Sk className="h-4 w-20 mx-auto" />
            </td>
            {/* Actions */}
            <td className="py-2 px-3 border">
              <div className="flex gap-2 justify-center">
                <Sk className="w-7 h-7 rounded-full" />
                <Sk className="w-7 h-7 rounded-full" />
                <Sk className="w-7 h-7 rounded-full" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ──────────────────────────────────────────────
// Row page: grid of row cards (3 cols lg, 2 sm)
// Matches: p-2 border rounded-lg bg-white shadow-md
// ──────────────────────────────────────────────
export const RowPageSkeleton = ({ count = 9 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="p-2 border rounded-lg shadow-md"
        style={{ background: "var(--surface)" }}
      >
        {/* Row header */}
        <Sk className="h-4 w-3/4 mx-auto mb-2" />
        <div className="border-b mb-2" />
        {/* Tiles badge + action icons */}
        <div className="flex justify-between items-center my-2">
          <Sk className="h-6 w-16 rounded-full" />
          <div className="flex items-center gap-3">
            <Sk className="w-4 h-4 rounded" />
            <Sk className="w-4 h-4 rounded" />
            <Sk className="w-4 h-4 rounded" />
            <Sk className="w-4 h-4 rounded" />
          </div>
        </div>
        {/* TilesList placeholder strip */}
        <Sk className="h-14 w-full rounded mt-1" />
      </div>
    ))}
  </div>
);

// ──────────────────────────────────────────────
// Scheduling: table rows (Sr No | Name | Start | End | Actions)
// ──────────────────────────────────────────────
export const SchedulingSkeleton = ({ count = 10 }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm border-collapse">
      <thead style={{ background: "var(--surface-2)" }}>
        <tr>
          {["Sr No", "Name", "Start Time", "End Time", "Actions"].map((h) => (
            <th key={h} className="border border-gray-300 px-4 py-3 text-left font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: count }).map((_, i) => (
          <tr key={i} style={{ background: "var(--surface)" }}>
            <td className="border px-4 py-3">
              <Sk className="h-4 w-6" />
            </td>
            <td className="border px-4 py-3">
              <Sk className="h-4 w-44" />
            </td>
            <td className="border px-4 py-3">
              <Sk className="h-4 w-28" />
            </td>
            <td className="border px-4 py-3">
              <Sk className="h-4 w-28" />
            </td>
            <td className="border px-4 py-3">
              <div className="flex gap-2">
                <Sk className="w-7 h-7 rounded-full" />
                <Sk className="w-7 h-7 rounded-full" />
                <Sk className="w-7 h-7 rounded-full" />
                <Sk className="w-7 h-7 rounded-full" />
                <Sk className="w-7 h-7 rounded-full" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ──────────────────────────────────────────────
// ShowAllTiles modal: 4-col tile card grid
// ──────────────────────────────────────────────
export const ShowAllTilesSkeleton = ({ count = 24 }) => (
  <div className="grid grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="relative bg-slate-50 rounded-lg p-4 border-2 border-gray-200"
      >
        {/* Tile image */}
        <Sk className="w-full h-48 rounded-md mb-2" />
        {/* Title */}
        <Sk className="h-4 w-3/4 mb-2" />
        {/* Source */}
        <Sk className="h-3 w-1/2 mb-1" />
        {/* Language */}
        <Sk className="h-3 w-2/3" />
      </div>
    ))}
  </div>
);

// ──────────────────────────────────────────────
// RowForm modal skeleton: mirrors the form layout
// ──────────────────────────────────────────────
export const RowFormSkeleton = () => (
  <div className="space-y-4 border-slate-300 shadow-md border bg-gray-100 p-4 mx-auto rounded-md max-w-6xl w-full">
    {/* Row Layout + Row Header */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1">
        <Sk className="h-3 w-20" />
        <Sk className="h-9 w-full rounded-md" />
      </div>
      <div className="space-y-1">
        <Sk className="h-3 w-24" />
        <Sk className="h-9 w-full rounded-md" />
      </div>
    </div>
    {/* Row Width + Row Height */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1">
        <Sk className="h-3 w-20" />
        <Sk className="h-9 w-full rounded-md" />
      </div>
      <div className="space-y-1">
        <Sk className="h-3 w-20" />
        <Sk className="h-9 w-full rounded-md" />
      </div>
    </div>
    {/* Row BG + Row Icon */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1">
        <Sk className="h-3 w-16" />
        <Sk className="h-9 w-full rounded-md" />
      </div>
      <div className="space-y-1">
        <Sk className="h-3 w-16" />
        <Sk className="h-9 w-full rounded-md" />
      </div>
    </div>
    {/* UTM fields */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-1">
          <Sk className="h-3 w-24" />
          <Sk className="h-9 w-full rounded-md" />
        </div>
      ))}
    </div>
    {/* Tile search */}
    <div className="space-y-1">
      <Sk className="h-3 w-28" />
      <Sk className="h-9 w-full rounded-md" />
    </div>
    {/* Selected tiles strip */}
    <div className="space-y-1">
      <Sk className="h-3 w-24" />
      <div className="flex gap-2 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <Sk key={i} className="flex-shrink-0 w-24 h-16 rounded-md" />
        ))}
      </div>
    </div>
    {/* Save button */}
    <div className="flex justify-end">
      <Sk className="h-9 w-28 rounded-md" />
    </div>
  </div>
);

// ──────────────────────────────────────────────
// RecentTiles: premium card grid (4 cols xl)
// Matches: bg-white rounded-2xl shadow-lg overflow-hidden
// ──────────────────────────────────────────────
export const RecentTilesSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 pb-6">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="rounded-2xl shadow-lg overflow-hidden border border-gray-100"
        style={{ background: "var(--surface)" }}
      >
        {/* Image area — aspect-[3/2] */}
        <Sk className="w-full aspect-[3/2]" style={{ borderRadius: 0 }} />
        {/* Info section */}
        <div className="p-4 space-y-3">
          {/* Title */}
          <Sk className="h-5 w-4/5" />
          {/* Source */}
          <Sk className="h-4 w-3/5" />
          {/* Action buttons row: 5 cols */}
          <div className="grid grid-cols-5 gap-1.5 pt-1">
            {Array.from({ length: 5 }).map((__, j) => (
              <Sk key={j} className="h-10 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);
